import type { QuickApplyProfile, Resume } from "~/quick_apply/types";
import type { SeekProfile } from "../utils/seek_loader.server";

/**
 * Parses a date string in "YYYY-MM" format and returns month and year
 */
function parseYearMonth(
  dateString: string | null | undefined
): { year: number; month: number } | null {
  if (!dateString) return null;

  const parts = dateString.split("-");
  if (parts.length !== 2) return null;

  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);

  if (isNaN(year) || isNaN(month) || month < 1 || month > 12) return null;

  return { year, month };
}

/**
 * Adapts a SeekProfile to QuickApplyProfile format so we can reuse
 * the existing QuickApply autofiller logic.
 *
 * Note: Resume is pre-processed server-side with S3 URL in the loader.
 */
export function adaptSeekProfileToQuickApplyProfile(seekProfile: SeekProfile): QuickApplyProfile {
  const person = seekProfile.candidate.person;

  const employments = (seekProfile.employment || [])
    .flatMap((emp) => {
      return (emp.positionHistories || []).map((position) => {
        const startParsed = parseYearMonth(position.start || null);
        const endParsed = position.current ? null : parseYearMonth(position.end || null);

        return {
          company: emp.organization?.name || "",
          title: position.title || "",
          startDate: startParsed || null,
          endDate: endParsed || null,
        };
      });
    })
    .filter((emp) => emp.company && emp.title && emp.startDate);

  // Use resume (pre-processed server-side with S3 URL) or default to empty
  const resume: Resume = seekProfile.resume || {
    name: "",
    url: "",
  };

  return {
    id: parseInt(seekProfile.profileId.value) || 0,
    status: "complete",
    firstName: person.name.given || null,
    lastName: person.name.family || null,
    email: person.communication?.email?.[0]?.address || "",
    phoneNumber: person.communication?.phone?.[0]?.formattedNumber || null,
    preferredName: person.name.formattedName || null,
    location: null,
    websites: {
      github: null,
      linkedin: null,
      portfolio: null,
      website: null,
    },
    educations: [],
    // @ts-expect-error - Filter ensures startDate is non-null, but TypeScript can't infer this
    employments,
    resume,
    selfIdentification: {
      disabilityStatus: null,
      gender: null,
      race: null,
      veteranStatus: null,
    },
    dreamJobAvailable: false,
  };
}
